import React, { useEffect, useState, useRef } from "react";
import Sidebar from "./Sidebar";
import "./AdminPackages.css";
import api, { API_ENDPOINTS } from "../config";

const emptyFeature = () => ({ name: "", price: "", enabled: true });

const initialForm = {
  name: "",
  pagename: "",
  allowCustom: false,
  features: [emptyFeature()],
};

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [selectedSubService, setSelectedSubService] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [editingPackage, setEditingPackage] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);
  const [notification, setNotification] = useState({ type: "", message: "" });

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: "", message: "" }), 5000);
  };

  // Fetch packages & services
  const fetchPackages = async () => {
    try {
      const res = await api.get(API_ENDPOINTS.GET_ALL_PACKAGES);
      setPackages(res.data.packages || []);
      console.log("services",res.data.packages);
    } catch (err) {
      console.error("fetchPackages error:", err);
      alert("Failed to fetch packages");
    }
  };

  const fetchServices = async () => {
    try {
      const res = await api.get(`${API_ENDPOINTS.GET_ALL_SERVICES}/`);
      setServices(res.data.services || []);
      console.log("services",res.data.services);
    } catch (err) {
      console.error("fetchServices error:", err);
      alert("Failed to fetch services");
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchServices();
  }, []);

  // Popup backdrop click
  useEffect(() => {
    const handleBackdropClick = (e) => {
      if (e.target.classList.contains('ik-popup-overlay')) setIsPopupOpen(false);
    };
    if (isPopupOpen) document.addEventListener('click', handleBackdropClick);
    return () => document.removeEventListener('click', handleBackdropClick);
  }, [isPopupOpen]);

  const openCreatePopup = () => {
    setFormData(initialForm);
    setSelectedSubService("");
    setEditingPackage(null);
    setSubServices([]);
    setIsPopupOpen(true);
  };

  const openEditPopup = (pkg) => {
    const featuresArray = [];
    if (pkg.features && typeof pkg.features === "object") {
      Object.entries(pkg.features).forEach(([key, val]) => {
        featuresArray.push({
          name: key,
          enabled: Boolean(val.enabled),
          price: val.price !== undefined && val.price !== null ? String(val.price) : "",
        });
      });
    }
    if (featuresArray.length === 0) featuresArray.push(emptyFeature());

    setFormData({
      name: pkg.name || "",
      pagename: pkg.pagename || "",
      allowCustom: pkg.allowCustom || false,
      features: featuresArray,
    });
    setSelectedSubService(pkg.subServiceId || "");
    setEditingPackage(pkg);
    setIsPopupOpen(true);

    // fetch sub-services for the selected service
    if (pkg.pagename) fetchSubServices(pkg.pagename);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    setEditingPackage(null);
    setFormData(initialForm);
    setSubServices([]);
    setSelectedSubService("");
  };

  // Feature handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleFeatureChange = (index, field, value) => {
    setFormData((s) => {
      const features = [...s.features];
      if (!features[index]) features[index] = emptyFeature();
      features[index] = { ...features[index], [field]: value };
      return { ...s, features };
    });
  };

  const handlePriceFocus = (index) => {
    setFormData((s) => {
      const features = [...s.features];
      if (features[index] && (features[index].price === "0" || features[index].price === 0)) {
        features[index].price = "";
      }
      return { ...s, features };
    });
  };

  const addFeature = () => setFormData((s) => ({ ...s, features: [...s.features, emptyFeature()] }));
  const removeFeature = (index) => setFormData((s) => {
    const features = s.features.filter((_, i) => i !== index);
    return { ...s, features: features.length ? features : [emptyFeature()] };
  });

  // Fetch sub-services when service changes
  const fetchSubServices = async (serviceId) => {
    try {
      const res = await api.get(`/api/subservices?serviceId=${serviceId}`);
      setSubServices(res.data.subservices || []);
    } catch (err) {
      console.error("Failed to fetch sub-services", err);
      setSubServices([]);
    }
  };

  const handleServiceChange = async (e) => {
    const serviceId = e.target.value;
    setFormData((s) => ({ ...s, pagename: serviceId }));
    setSelectedSubService("");
    if (serviceId) fetchSubServices(serviceId);
    else setSubServices([]);
  };

  // Submit package
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return alert("Package name is required");
    if (!formData.pagename.trim()) return alert("Service (pagename) is required");

  // ✅ Allow all letters (any language), digits, and spaces
// ✅ Allow Amharic, English letters, digits, and spaces
const validNamePattern = /^[\u0600-\u06FF\u0750-\u077F\u1200-\u137F\u1380-\u139F\u2D80-\u2DDFA-Za-z0-9\s%\-_\/\\|,*\[\]\(\)\{\}\<\>"':]+$/;


let hasInvalidFeature = false; // 👈 Track invalid entries

    const featuresMap = {};
    formData.features.forEach((f) => {
      const featureName = (f.name || "").trim();
      if (!featureName) return;
    // ✅ Validate feature name letters (Amharic/English), numbers, and spaces.
  if (!validNamePattern.test(featureName)) {
    showNotification(
      "error",
      `Feature "${featureName}" can only contain letters (Amharic/English), numbers, and spaces.`
    );
    hasInvalidFeature = true; // 👈 Flag invalid but don't remove input
    return; // Skip adding this feature, but keep it visible in the form
  }
      const enabled = !!f.enabled;
      const price = enabled ? Number(f.price || 0) : 0;
      featuresMap[featureName] = { enabled, price };
    });

    // ✅ Stop submission entirely if any invalid feature exists
if (hasInvalidFeature) {
  return; // Do not proceed to submit/update
}

    const payload = {
      name: formData.name.trim(),
      pagename: formData.pagename.trim(),
      allowCustom: formData.allowCustom,
      features: featuresMap,
      subServiceId: selectedSubService || null,
    };

    try {
      const url = editingPackage
        ? `${API_ENDPOINTS.UPDATE_PACKAGES}/${editingPackage._id}`
        : `${API_ENDPOINTS.CREATE_PACKAGES}`;
      const method = editingPackage ? "put" : "post";

      const res = await api({ method, url, data: payload, withCredentials: true });
      showNotification("success", editingPackage ? "Package updated ✅" : "Package created ✅");
      await fetchPackages();
      closePopup();
    } catch (err) {
      console.error("handleSubmit error:", err);
      showNotification("error", editingPackage ? "Failed to update ❌" : "Failed to create ❌");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`${API_ENDPOINTS.DELETE_PACKAGES}/${id}`);
      showNotification("success", "Package deleted successfully ✅");
      fetchPackages();
    } catch (err) {
      console.error("delete error:", err);
      showNotification("error", "Failed to delete package ❌");
    }
  };

  const totalPrice = formData.features.reduce((sum, f) => sum + (f.enabled ? Number(f.price || 0) : 0), 0);

  const filteredPackages = packages.filter((pkg) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
  (pkg.name || "").toLowerCase().includes(q) ||
  (pkg.service?.name || pkg.pagename?.servicename || "").toLowerCase().includes(q) ||
  (pkg.subServiceName || "").toLowerCase().includes(q)
);

  });

  return (
    <div className="ik-admin-container">
      <Sidebar />

      <main className="ik-main">
        {notification.message && (
          <div className={`ik-notification ${notification.type}`}>
            <span>{notification.message}</span>
            <button className="ik-close-btn" onClick={() => setNotification({ type: "", message: "" })}>✖</button>
          </div>
        )}

        <header className="ik-header">
          <h1 className="ik-title">Packages</h1>

          <div className="ik-header-controls">
            <div className="ik-search-wrap">
              <input
                id="ik-search-input"
                ref={searchInputRef}
                className="ik-search-input"
                placeholder="Search packages or services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button className="ik-search-btn" onClick={() => searchInputRef.current?.focus()} title="Search" aria-label="Search">🔍</button>
            </div>

            <button className="ik-create-btn" onClick={openCreatePopup}>+ Create Package</button>
          </div>
        </header>

        <section className="ik-table-wrap">
          <table className="ik-packages-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Service</th>
                <th>Sub Service</th>
                <th>Price</th>
                <th>Features</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPackages.length ? filteredPackages.map((pkg) => {
                const pkgTotal = Object.values(pkg.features || {}).reduce((s, ft) => s + (ft.enabled ? Number(ft.price || 0) : 0), 0);
                return (
                  <tr key={pkg._id}>
                    {/* <td>{pkg.name}</td>
                    <td>{pkg.pagename}{pkg.subServiceId ? ` / ${pkg.subServiceId}` : ""}</td>
                    <td>{pkg.subServiceId}</td> */}
                    <td>{pkg.name}</td>
                    {/* <td>{pkg.serviceName || "—"}</td>
                    <td>{pkg.subServiceName || "—"}</td> */}
                    <td>{pkg.service?.name || pkg.pagename?.servicename || "—"}</td>
 <td>{pkg.subServiceName || "—"}</td>


                    <td>{pkgTotal} Birr</td>
                    <td>
                      {Object.entries(pkg.features || {}).map(([k, v]) => (
                        <div key={k} className={`ik-feature-row ${v.enabled ? "ik-enabled" : "ik-disabled"}`}>
                          {v.enabled ? "✅" : "❌"} {k}{v.enabled ? <span className="ik-feature-price"> — {v.price} Birr</span> : null}
                         </div>
                      ))}
                    </td>
                    <td>
                      <button className="ik-edit-btn" onClick={() => openEditPopup(pkg)}>✏️</button>
                      <button className="ik-delete-btn" onClick={() => handleDelete(pkg._id)}>🗑</button>
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="5" className="ik-no-data">No packages found</td></tr>
              )}
            </tbody>
          </table>
        </section>
      </main>

      {isPopupOpen && (
        <div className="ik-popup-overlay" role="dialog" aria-modal="true">
          <div className="ik-popup-box" onClick={(e) => e.stopPropagation()}>
            <button className="ik-popup-close" onClick={closePopup} aria-label="Close">✖</button>

            <div className="ik-popup-content">
              <h2 className="ik-popup-title">{editingPackage ? "Edit Package" : "Create Package"}</h2>

              <form onSubmit={handleSubmit} className="ik-popup-form">
                <label className="ik-label">Package name</label>
                <input
                  className="ik-input"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Halal Wedding"
                  required
                />

                <label className="ik-label">Service (page)</label>
                <select
                  className="ik-select"
                  name="pagename"
                  value={formData.pagename}
                  onChange={handleServiceChange}
                  required
                >
                  <option value="">Choose service...</option>
                  {services.map((s) => (
                    <option key={s._id} value={s._id}>{s.servicename}</option>
                  ))}
                </select>

                {subServices.length > 0 && (
                  <>
                    <label className="ik-label">Sub-Service (optional)</label>
                    <select
                      className="ik-select"
                      value={selectedSubService}
                      onChange={(e) => setSelectedSubService(e.target.value)}
                    >
                      <option value="">Choose sub-service...</option>
                      {subServices.map((sub) => (
                        <option key={sub._id} value={sub._id}>{sub.name}</option>
                      ))}
                    </select>
                  </>
                )}

                <label className="ik-checkbox-label">
  <input
    type="checkbox"
    checked={formData.allowCustom}
    onChange={(e) =>
      setFormData((s) => ({ ...s, allowCustom: e.target.checked }))
    }
  />
  Allow users to pick features individually
</label>

                <h3 className="ik-subtitle">Features</h3>

                {formData.features.map((feat, i) => (
                  <div className="ik-feature-edit" key={i}>
                    <input
                      className="ik-input ik-feature-name"
                      placeholder="Feature name"
                      value={feat.name}
                      onChange={(e) => handleFeatureChange(i, "name", e.target.value)}
                      required
                    />
                    <input
                      className="ik-input ik-feature-price"
                      placeholder="Price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={feat.price}
                      onFocus={() => handlePriceFocus(i)}
                      onChange={(e) => handleFeatureChange(i, "price", e.target.value)}
                      required
                    />
                    <label className="ik-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!feat.enabled}
                        onChange={(e) => handleFeatureChange(i, "enabled", e.target.checked)}
                      />
                      Enabled
                    </label>
                    <button type="button" className="ik-remove-feat-btn" onClick={() => removeFeature(i)} aria-label="Remove feature">✖</button>
                  </div>
                ))}

                <div className="ik-features-actions">
                  <button type="button" className="ik-add-feat-btn" onClick={addFeature}>+ Add feature</button>
                </div>

                <div className="ik-total">
                  <strong>Total: {totalPrice} Birr</strong>
                </div>

                <div className="ik-popup-actions">
                  <button type="submit" className="ik-save-btn">{editingPackage ? "Update" : "Create"}</button>
                  <button type="button" className="ik-cancel-btn" onClick={closePopup}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPackages;
